import { useDeepLinks } from "src/hooks/useDeepLinks";
import {
  AnnotatedDeepLink,
  DeepLinkType,
  DigestV2State,
  ExploreCard,
} from "pages/api/deep-links/types";
import { useMemo } from "react";
import { UseQueryResult } from "react-query";

export function useDigestLinks(link: AnnotatedDeepLink<DigestV2State>) {
  const digests = useDeepLinks(DeepLinkType.DIGEST_2);
  const queries = useDeepLinks(DeepLinkType.EXPLORE);
  const boards = useDeepLinks(DeepLinkType.BOARD);

  const links = useMemo(() => {
    if (!link || !link.json || !link.json.data) return [];
    const defaultLinks = Object.entries(link.json.data).map(([id, state]) => ({
      id,
      type: DeepLinkType.EXPLORE,
      json: state,
    })) as AnnotatedDeepLink<ExploreCard>[]; // fake them as explore deeplinks
    defaultLinks.sort((a, b) => a.json.title.localeCompare(b.json.title));

    const isLoading =
      digests.links.isLoading ||
      queries.links.isLoading ||
      boards.links.isLoading;
    if (isLoading) return [];
    const digestLinks = link.json.links;
    if (!digestLinks || digestLinks.length === 0) return defaultLinks;

    // hydrate
    const lookup = {
      [DeepLinkType.DIGEST_2]: digests.links,
      [DeepLinkType.EXPLORE]: queries.links,
      [DeepLinkType.BOARD]: boards.links,
    } as Record<string, UseQueryResult<AnnotatedDeepLink<any>[]>>;

    return digestLinks
      .map(({ id, type }) => lookup[type]?.data?.find((l) => l.id === id))
      .filter(Boolean) as AnnotatedDeepLink<any>[];
  }, [link, digests, queries, boards]);

  return {
    links,
    isLoading:
      digests.links.isLoading ||
      queries.links.isLoading ||
      boards.links.isLoading,
  };
}
